/**
 * Client d'API de l'ESPACE CLIENT.
 *
 * Ce module ne connaît que `/api/espace-client/*`. Il n'importe rien de
 * `lib/api.ts`, et ses types ne comportent volontairement aucun champ
 * financier : le §12.2 interdit de montrer au client les prix d'achat, les
 * marges, les commissions et les commentaires internes. Ils ne sont pas
 * masqués ici — ils ne sont pas là, et il n'existe aucun chemin pour aller les
 * chercher (ADR-0012).
 */

import { appeler } from "@/lib/http";

export { ErreurApi } from "@/lib/http";

export type IdentiteClient = {
  id: string;
  email: string;
};

/** Ce qu'un client voit d'un lot. Volontairement pauvre. */
export type LotClient = {
  id: string;
  code_lot: string;
  intitule: string;
  // Celle de l'adresse de réalisation ; absente à l'emport sans pose.
  ville: string | null;
  statut_affiche: string;
};

export type DocumentClient = {
  id: string;
  categorie: string;
  nom_fichier_origine: string;
  type_mime: string;
  taille_octets: number;
  publie_le: string | null;
};

export const apiClient = {
  connexion: (email: string, motDePasse: string) =>
    appeler<IdentiteClient>("/api/espace-client/connexion", {
      methode: "POST",
      corps: { email, mot_de_passe: motDePasse },
    }),
  deconnexion: () => appeler<void>("/api/espace-client/deconnexion", { methode: "POST" }),
  moi: (signal?: AbortSignal) => appeler<IdentiteClient>("/api/espace-client/moi", { signal }),
  lots: (signal?: AbortSignal) =>
    appeler<LotClient[]>("/api/espace-client/lots", { signal }),
  documents: (signal?: AbortSignal) =>
    appeler<DocumentClient[]>("/api/espace-client/documents", { signal }),
  // Aucun document n'est servi en statique (ADR-0011) : le lien passe par le
  // point d'API, qui vérifie la publication ET le rattachement au client.
  lienDocument: (id: string) => `/api/espace-client/documents/${id}/contenu`,
};

const LIBELLES_STATUT: Record<string, string> = {
  EN_COURS: "En cours",
  EN_COURS_DE_POSE: "Pose en cours",
  RECEPTIONNE_SANS_RESERVES: "Réceptionné",
  RECEPTIONNE_AVEC_RESERVES: "Réceptionné, reprises en cours",
  CLOTURE_OPERATIONNELLE: "Terminé",
  ANNULE: "Annulé",
};

export function libelleStatut(code: string) {
  return LIBELLES_STATUT[code] ?? code.replaceAll("_", " ").toLowerCase();
}

const LIBELLES_CATEGORIE: Record<string, string> = {
  PLAN_VALIDE: "Plan validé",
  PLAN: "Plan",
  RENDU_3D: "Rendu 3D",
  DEVIS: "Devis",
  PV_RECEPTION: "Procès-verbal de réception",
  PV_LEVEE_RESERVES: "Procès-verbal de levée des réserves",
  FICHE_TVA_10: "Fiche TVA 10 %",
  PHOTO_CHANTIER: "Photo de chantier",
  AUTRE: "Document",
};

export function libelleCategorie(code: string) {
  return LIBELLES_CATEGORIE[code] ?? "Document";
}

export function tailleLisible(octets: number) {
  if (octets < 1024) return `${octets} o`;
  if (octets < 1024 * 1024) return `${Math.round(octets / 1024)} Ko`;
  return `${(octets / (1024 * 1024)).toFixed(1)} Mo`;
}
